// 智能客服 widget (AI Concierge) — 跨 App 共用的常駐客服問答介面.
//
// 接 restful proxy POST /v3/ai/concierge/chat (server-side 代簽 cs-copilot HMAC;
// 見 restful-api adapters/cscopilot)。client 端只帶一般 client gate header,不持密鑰。
// 凍結契約 APP/specs/services/ai_concierge.md §3：常駐入口 + 對話 UI + 來源卡 +
// 評價。本實作對應 cs-copilot 實際能力（RAG 問答 + sources + up/down），SSE/工具/
// 轉真人為後端缺口（另開 issue 給 cs-copilot 端）。
//
// 自包：無外部相依、props 驅動（apiBase / clientId / clientKey / title），各 App
// 殼以自身 config 掛載一個（IA 決定位置）。樣式用 base-token 變數，跟隨各 App 主題。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { navigateSpa, resolveConciergeLink, type ConciergeLinkAction } from './deepLinks';
import { ConciergeSSEDecoder, type ConciergeEvent } from './sse';
import { subscribeConciergeOpen } from './conciergePresenter';
import './ConciergeWidget.css';

interface Source {
  title: string;
  url?: string;
}
interface Msg {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  sources?: Source[];
  messageId?: string; // assistant only: cs message_id for feedback
  status?: string; // assistant only: answered | no_answer | out_of_scope
  rated?: 'up' | 'down';
  error?: boolean;
}

export interface ConciergeWidgetProps {
  apiBase: string;
  clientId: string;
  clientKey: string;
  /** Build 版本 → X-Client-Version(APP#53);未給則不帶。 */
  clientVersion?: string;
  /**
   * 入口形態(IA 決定,`services/ai_concierge.md` §3.1「確切落點由各 App 殼 IA 決定」):
   * - `floating`(預設,維持既有行為)= 自帶右下角常駐浮動鈕。
   * - `headless` = **不畫任何入口鈕**,只在 `openConcierge()` 被呼叫時才呈現對話面板。
   *   Home 走這個:業主裁決 **D69**(`apps/home/specs/ia.md` §5.1)否決全域浮動 FAB
   *   ——理由是 Material 對 FAB 的定義是「triggers the primary action」(客服在任何
   *   畫面都不是主要動作),且 WCAG 2.2 SC 2.4.11(Focus Not Obscured)不容許浮層
   *   完全遮蔽取得焦點的元件。入口改由帳號→說明中心 + 各頁情境內文入口提供。
   */
  variant?: 'floating' | 'headless';
  /** 標題（各 App 品牌文案，預設「智能客服」）。 */
  title?: string;
  /** 開場白。 */
  greeting?: string;
}

function uuid(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'cv-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Derive the §4.2 context.app from the client id (c-ls-{app}-web / c-ls-web=home), so
// the concierge answer can be on-topic per app. `app` is style/context only — auth is
// always the token scope (ai_concierge §4.2).
function appFromClientId(clientId: string): string {
  const m = /^c-ls-([a-z]+)-web$/.exec(clientId);
  return m ? m[1] : 'home'; // c-ls-home-web → home;legacy c-ls-web fallback → home
}

// Stable per-browser id sent as X-LS-Device so the proxy rate-limits per end user
// (cs-copilot is 6 chats/min/device; without a per-user id all users share one
// bucket). Persisted in localStorage; per-session fallback if storage is blocked.
function deviceId(): string {
  try {
    const k = 'ls_cs_device';
    let d = localStorage.getItem(k);
    if (!d) {
      d = uuid();
      localStorage.setItem(k, d);
    }
    return d;
  } catch {
    return uuid();
  }
}

// Stream the SSE concierge response into one assistant bubble (filled as tokens
// arrive). kb_result → source chips, escalation → out_of_scope status, error → error
// bubble. Empty turn → graceful fallback text. Feedback rating is unavailable on SSE
// turns (cs streams no message_id) — degrades to no rating buttons, never an error.
async function consumeConciergeSSE(
  body: ReadableStream<Uint8Array>,
  convId: { current: string },
  setMsgs: (updater: (m: Msg[]) => Msg[]) => void
): Promise<void> {
  const aid = uuid();
  setMsgs((m) => [...m, { id: aid, role: 'assistant', text: '' }]);
  const reader = body.getReader();
  const td = new TextDecoder();
  const dec = new ConciergeSSEDecoder();
  let gotContent = false;
  const apply = (ev: ConciergeEvent) => {
    switch (ev.type) {
      case 'conversation':
        if (ev.id) convId.current = ev.id;
        break;
      case 'token':
        gotContent = true;
        setMsgs((m) => m.map((x) => (x.id === aid ? { ...x, text: x.text + ev.text } : x)));
        break;
      case 'kb_result': {
        const sources = ev.results.filter((r) => r.title).map((r) => ({ title: r.title, url: r.deep_link || undefined }));
        if (sources.length) {
          gotContent = true;
          setMsgs((m) => m.map((x) => (x.id === aid ? { ...x, sources } : x)));
        }
        break;
      }
      case 'escalation':
        gotContent = true;
        setMsgs((m) => m.map((x) => (x.id === aid ? { ...x, status: 'out_of_scope' } : x)));
        break;
      case 'error':
        setMsgs((m) =>
          m.map((x) => (x.id === aid ? { ...x, text: x.text || ev.message || i18n.t('concierge.serviceUnavailable'), error: true } : x))
        );
        break;
      default:
        break; // done / subscription / ticket / unknown — no UI here
    }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value) for (const ev of dec.push(td.decode(value, { stream: true }))) apply(ev);
  }
  for (const ev of dec.push(td.decode())) apply(ev); // flush any trailing multibyte
  for (const ev of dec.flush()) apply(ev);
  if (!gotContent) {
    setMsgs((m) => m.map((x) => (x.id === aid && !x.text ? { ...x, text: i18n.t('concierge.noAnswer') } : x)));
  }
}

export function ConciergeWidget({ apiBase, clientId, clientKey, clientVersion, variant = 'floating', title: titleProp, greeting: greetingProp }: ConciergeWidgetProps) {
  const { t } = useTranslation();
  const title = titleProp ?? t('concierge.title');
  const greeting = greetingProp ?? t('concierge.greeting');
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const convId = useRef<string>(uuid());
  const dev = useRef<string>(deviceId());
  const listRef = useRef<HTMLDivElement>(null);

  // 程式化開啟通道(D69):說明中心主入口 / 各頁情境入口 / cs_reply 續會話都走這裡。
  // 兩種 variant 都訂閱 —— `floating` 也可能被情境入口叫開,少一條 if 就少一種分歧。
  useEffect(() => subscribeConciergeOpen((req) => {
    if (req.conversationId) convId.current = req.conversationId;
    setOpen(true);
  }), []);

  const headers = {
    'Content-Type': 'application/json',
    'X-Client-Id': clientId,
    'X-Client-Key': clientKey,
    ...(clientVersion ? { 'X-Client-Version': clientVersion } : {}),
    'X-LS-Device': dev.current,
  };

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ id: uuid(), role: 'assistant', text: greeting }]);
    }
  }, [open, msgs.length, greeting]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // jsdom 沒有 Element.prototype.scrollTo → 直接呼叫會在測試環境丟 TypeError,
    // 讓任何「開啟對話面板」的測試都跑不起來(Aura 的同一份 widget 早就這樣防)。
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
    } else {
      list.scrollTop = list.scrollHeight;
    }
  }, [msgs, busy]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    const userMsg: Msg = { id: uuid(), role: 'user', text };
    setMsgs((m) => [...m, userMsg]);
    setBusy(true);
    try {
      const res = await fetch(apiBase + '/v3/ai/concierge/chat', {
        method: 'POST',
        headers: { ...headers, Accept: 'text/event-stream' },
        body: JSON.stringify({ conversation_id: convId.current, message: text, context: { app: appFromClientId(clientId), surface: 'web' } }),
      });
      const ct = res.headers.get('content-type') || '';
      if (res.ok && ct.includes('text/event-stream') && res.body) {
        // SSE path (cs streams token-by-token via the restful proxy). Fill one assistant
        // bubble as events arrive. Dual-mode: a JSON backend falls through to the legacy
        // branch below, so this widget works against both — the backend can flip to SSE
        // with no client change (ai_concierge.md §1).
        await consumeConciergeSSE(res.body, convId, setMsgs);
      } else {
        const json = (await res.json().catch(() => ({}))) as {
          data?: { message_id?: string; reply?: { status?: string; answer?: string; sources?: Source[] } };
        };
        if (!res.ok || !json.data?.reply) {
          throw new Error(t('concierge.serviceUnavailableShort'));
        }
        const r = json.data.reply;
        setMsgs((m) => [
          ...m,
          {
            id: uuid(),
            role: 'assistant',
            text: r.answer || t('concierge.noAnswer'),
            sources: r.sources?.filter((s) => s.title),
            messageId: json.data?.message_id,
            status: r.status,
          },
        ]);
      }
    } catch {
      setMsgs((m) => [...m, { id: uuid(), role: 'assistant', text: t('concierge.serviceUnavailable'), error: true }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, apiBase, clientId, clientKey, t]);

  const rate = useCallback(
    async (msg: Msg, rating: 'up' | 'down') => {
      if (!msg.messageId || msg.rated) return;
      setMsgs((m) => m.map((x) => (x.id === msg.id ? { ...x, rated: rating } : x)));
      try {
        await fetch(apiBase + '/v3/ai/concierge/feedback', {
          method: 'POST',
          headers,
          body: JSON.stringify({ conversation_id: convId.current, message_id: msg.messageId, rating }),
        });
      } catch {
        /* feedback 失敗不影響對話 */
      }
    },
    [apiBase, clientId, clientKey]
  );

  const openSource = useCallback((action: ConciergeLinkAction) => {
    if (action.kind === 'internal') {
      navigateSpa(action.href);
      setOpen(false);
      return;
    }
    if (action.kind === 'support') {
      if (action.conversationId) convId.current = action.conversationId;
      setOpen(true);
    }
  }, []);

  const renderSource = (s: Source, i: number) => {
    const action = resolveConciergeLink(s.url);
    if (action.kind === 'internal') {
      return (
        <a
          key={i}
          className="cs-source"
          href={action.href}
          onClick={(e) => {
            e.preventDefault();
            openSource(action);
          }}
        >
          {s.title}
        </a>
      );
    }
    if (action.kind === 'support') {
      return (
        <button key={i} type="button" className="cs-source" onClick={() => openSource(action)}>
          {s.title}
        </button>
      );
    }
    if (action.kind === 'external') {
      return (
        <a key={i} className="cs-source" href={action.href} target="_blank" rel="noopener noreferrer">
          {s.title}
        </a>
      );
    }
    return <span key={i} className="cs-source">{s.title}</span>;
  };

  // D69:headless 關閉時**完全不渲染**——不是隱藏一顆鈕,而是連 position:fixed 的
  // `.cs-concierge` 容器都不進 DOM,浮層不可能遮到任何取得焦點的元件。
  if (variant === 'headless' && !open) return null;

  return (
    <div className="cs-concierge">
      {!open && (
        <button className="cs-fab" onClick={() => setOpen(true)} aria-label={t('concierge.open', { title })} title={title}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 3C7 3 3 6.6 3 11c0 2.1.9 4 2.5 5.4L5 21l4.2-1.6c.9.2 1.8.4 2.8.4 5 0 9-3.6 9-8s-4-9-9-9z" fill="currentColor" />
          </svg>
        </button>
      )}
      {open && (
        <div className="cs-panel" role="dialog" aria-label={title}>
          <div className="cs-head">
            <span className="cs-title">{title}</span>
            <button className="cs-close" onClick={() => setOpen(false)} aria-label={t('common.close')}>✕</button>
          </div>
          <div className="cs-list" ref={listRef}>
            {msgs.map((m) => (
              <div key={m.id} className={`cs-msg cs-msg--${m.role}${m.error ? ' cs-msg--err' : ''}`}>
                <div className="cs-bubble">{m.text}</div>
                {m.sources && m.sources.length > 0 && (
                  <div className="cs-sources">
                    {m.sources.map(renderSource)}
                  </div>
                )}
                {m.role === 'assistant' && m.messageId && (
                  <div className="cs-rate">
                    <button className={`cs-rate-btn${m.rated === 'up' ? ' is-on' : ''}`} disabled={!!m.rated} onClick={() => rate(m, 'up')} aria-label={t('concierge.helpful')}>👍</button>
                    <button className={`cs-rate-btn${m.rated === 'down' ? ' is-on' : ''}`} disabled={!!m.rated} onClick={() => rate(m, 'down')} aria-label={t('concierge.notHelpful')}>👎</button>
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="cs-msg cs-msg--assistant"><div className="cs-bubble cs-typing"><span></span><span></span><span></span></div></div>}
          </div>
          <form
            className="cs-input"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('concierge.inputPlaceholder')}
              aria-label={t('concierge.inputLabel')}
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} aria-label={t('concierge.send')}>{t('concierge.send')}</button>
          </form>
        </div>
      )}
    </div>
  );
}
